import type {
  OneClickImportEntryPoint,
  OneClickImportSource,
} from "@/store/one-click-import/types";
import type { MeetingProvider } from "@/domains/calendar/getMeetingPlatformFromUrl";
import type { ChatModel, ChatModelFamily } from "@/domains/chat-model";
import type { AudioRecordingStrategy } from "@/store/audio-recordings/types";
import type { GatedFeature } from "@/store/feature-gates/types";
import { EventContext } from "./context";

export enum TrackedEvent {
  NoteView = "note_view",
  NotesView = "notes_view",
  ImportedNotesView = "imported_notes_view",
  ImportViaCreateNote = "import_via_create_note",
  ImportViaUserProfileMenu = "import_via_user_profile_menu",
  NoteCreate = "note_create",
  NoteAddToCollection = "note_add_to_collection",
  AutoOrganizeNote = "auto_organize_note",
  NoteShare = "note_share",
  TemplateCreate = "template_create",
  TemplateUsed = "template_used",
  TemplateView = "template_view",
  TemplatesView = "templates_view",
  CollectionView = "collection_view",
  CollectionsView = "collections_view",
  ImportedCollectionsView = "imported_collections_view",
  CollectionCreate = "collection_create",
  CollectionShare = "collection_share",
  TemplateShare = "template_share",
  ChatView = "chat_view",
  ChatCreate = "chat_create",
  ChatCreateFromConversationTooLong = "chat_create_from_conversation_too_long",
  ChatResume = "chat_resume",
  ChatSelect = "chat_select",
  ChatMessageSend = "chat_message_send",
  ChatMessageSendFromSuggestion = "chat_message_send_from_suggestion",
  ChatMessageUpvote = "chat_message_upvote",
  ChatMessageDownvote = "chat_message_downvote",
  ChatInputLookAtClick = "chat_input_look_at_click",
  ChatInputAgentModeToggle = "chat_input_agent_mode_toggle",
  ChatInputContextAdd = "chat_input_context_add",
  ChatInputContextRemove = "chat_input_context_remove",
  SearchView = "search_view",
  SearchResultsOpenNote = "search_results_open_note",
  SearchResultsOpenCollection = "search_results_open_collection",
  ViewNoteAttachmentPdf = "view_note_attachment_pdf",
  QuickSearchView = "quick_search_view",
  QuickSearchOpenCollection = "quick_search_open_collection",
  QuickSearchOpenNote = "quick_search_open_note",
  ImportFrom1Dot0StartImport = "import_from_1_dot_0_start_import",
  ExportCreate = "export_create",
  PerformanceHeartbeat = "performance_heartbeat",
  SyncErrorShown = "sync_error_shown",
  AppCalloutBannerActionClicked = "app_callout_banner_action_clicked",
  AppCalloutBannerDismissed = "app_callout_banner_dismissed",
  PerformanceBootstrap = "performance_bootstrap",
  AppStoreInitErrorShown = "app_store_init_error_shown",
  ImportFilesDropped = "import_files_dropped",
  ImportFilesBatchStarted = "import_files_batch_started",
  ImportFilesBatchFailed = "import_files_batch_failed",

  // Note actions
  NoteCopyAsMarkdown = "note_copy_as_markdown",
  NotePasteMarkdown = "note_paste_markdown",

  // Email Capture Events
  EmailConnect = "email_connect",
  EmailDisconnect = "email_disconnect",
  EmailFormattingModeSelect = "email_formatting_mode_select",
  EmailFormattingSmartModeCustomize = "email_formatting_smart_mode_customize",
  EmailOrganizingModeChecked = "email_organizing_mode_checked",
  EmailOrganizingModeUnchecked = "email_organizing_mode_unchecked",
  EmailOrganizingSpecificCollectionsSaved = "email_organizing_specific_collections_saved",
  EmailOrganizingSmartModeCustomize = "email_organizing_smart_mode_customize",

  // Text Message Events
  TextFormattingModeSelect = "text_formatting_mode_select",
  TextFormattingSmartModeCustomize = "text_formatting_smart_mode_customize",
  TextOrganizingModeChecked = "text_organizing_mode_checked",
  TextOrganizingModeUnchecked = "text_organizing_mode_unchecked",
  TextOrganizingSpecificCollectionsSaved = "text_organizing_specific_collections_saved",
  TextOrganizingSmartModeCustomize = "text_organizing_smart_mode_customize",

  // API Request Events
  ApiKeyCreate = "api_key_create",
  ApiKeyName = "api_key_name",
  ApiKeyDelete = "api_key_delete",
  ApiDevDocsLinkClick = "api_dev_docs_link_click",

  // Zapier Events
  ZapierIntegrationLinkClick = "zapier_integration_link_click",
  ZapierDisconnect = "zapier_disconnect",

  // Voice Session Events
  VoiceSessionStart = "voice_session_start",
  VoiceSessionPause = "voice_session_pause",
  VoiceSessionResume = "voice_session_resume",
  VoiceSessionDone = "voice_session_done",
  VoiceSessionSuggestedEditsView = "voice_session_suggested_edits_view",
  VoiceSessionSuggestedEditsRefine = "voice_session_suggested_edits_refine",
  VoiceSessionSuggestedEditsAccept = "voice_session_suggested_edits_accept",
  VoiceSessionSuggestedEditsReject = "voice_session_suggested_edits_reject",
  VoiceRecordingView = "voice_recording_view",
  VoiceRecordingAudioPlay = "voice_recording_audio_play",
  VoiceRecordingTranscriptScroll = "voice_recording_transcript_scroll",
  DesktopSystemAudioPermissionsModalView = "desktop_system_audio_permissions_modal_view",
  DesktopSystemAudioPermissionInteract = "desktop_system_audio_permission_interact",
  DesktopSystemAudioPermissionOpenPreferences = "desktop_system_audio_permission_open_preferences",
  DesktopSystemAudioPermissionsRestartClick = "desktop_system_audio_permissions_restart_click",

  // Received from Electron
  MeetingDetectionNotificationView = "meeting_detection_notification_view",
  MeetingDetectionNotificationHover = "meeting_detection_notification_hover",
  MeetingDetectionNotificationDismiss = "meeting_detection_notification_dismiss",
  MeetingDetectionNotificationVoiceSessionStart = "meeting_detection_notification_voice_session_start",

  // Note Cleanup Events
  NoteCleanupStart = "note_cleanup_start",
  NoteCleanupRefine = "note_cleanup_refine",
  NoteCleanupAccept = "note_cleanup_accept",
  NoteCleanupReject = "note_cleanup_reject",

  // Welcome & Onboarding
  WelcomeOverlayView = "welcome_overlay_view",
  NativeUpsellView = "native_upsell_view",
  NativeUpsellSkip = "native_upsell_skip",
  NativeUpsellDownload = "native_upsell_download",
  IcpRfiStepComplete = "icp_rfi_step_complete",
  UseCaseStepComplete = "use_case_step_complete",
  ReferrerStepComplete = "referrer_step_complete",
  OnboardingVoiceModeView = "onboarding_voice_mode_view",
  OnboardingVoiceModeOptionSelect = "onboarding_voice_mode_option_select",
  OnboardingVoiceModeSkip = "onboarding_voice_mode_skip",
  OnboardingDownloadClick = "onboarding_download_click",
  OnboardingStepChange = "onboarding_step_change",
  OnboardingSubstepChange = "onboarding_substep_change",
  OnboardingComplete = "onboarding_complete",

  ImportCtaView = "import_cta_view",
  ImportWizardView = "import_wizard_view",
  ImportWizardAppSelect = "import_wizard_app_select",
  ImportWizardSkip = "import_wizard_skip",
  ImportWizardStepChange = "import_wizard_step_change",
  ImportWizardComplete = "import_wizard_complete",

  // OneClick Import Events
  OneClickImportListView = "one_click_import_list_view",
  OneClickImportItemSelect = "one_click_import_item_select",
  OneClickImportItemDeselect = "one_click_import_item_deselect",
  OneClickImportCancel = "one_click_import_cancel",
  OneClickImportSubmit = "one_click_import_submit",
  OneClickImportComplete = "one_click_import_complete",
  OneClickImportLoadFailed = "one_click_import_load_failed",

  // Multi-Tab Events
  PaneSplit = "pane_split",
  TabOpen = "tab_open",
  TabView = "tab_view",

  //Chrome Extension
  ChromeExtensionOnboardingInstallerView = "chrome_extension_onboarding_installer_view",
  ChromeExtensionOnboardingInstallerClick = "chrome_extension_onboarding_installer_click",
  ChromeExtensionHomeCarouselInstallerClick = "chrome_extension_home_carousel_installer_click",
  ChatGlobalInstructionsSave = "chat_global_instructions_save",
  ChromeExtensionCustomInstructionsSave = "chrome_extension_custom_instructions_save",
  ChromeExtensionModelSelect = "chrome_extension_model_select",
  ChromeExtensionModelDropdownOpen = "chrome_extension_model_dropdown_open",

  // Session Chat
  ChatSessionMessageSend = "chat_session_message_send",
  ChatSessionMessageUpvote = "chat_session_message_upvote",
  ChatSessionMessageDownvote = "chat_session_message_downvote",
  ChatPanelView = "chat_panel_view",
  ChatModelSelect = "chat_model_select",
  ChatModelDropdownOpen = "chat_model_dropdown_open",
  SidePanelView = "side_panel_view",

  // Monetization / Paywall
  PaywallView = "paywall_view",
  PaywallCancelClick = "paywall_cancel_click",
  PaywallUpgradeClick = "paywall_upgrade_click",
  PaywallUpgradeReturn = "paywall_upgrade_return",
  PaywallCancelReturn = "paywall_cancel_return",
  FeatureGateBannerView = "feature_gate_banner_view",
  FeatureGateBannerUpgradeClick = "feature_gate_banner_upgrade_click",
  FeatureGateDenied = "feature_gate_denied",

  // Heads Up
  HeadsUpItemHover = "heads_up_item_hover",
  HeadsUpItemClick = "heads_up_item_click",
  HeadsUpRefreshClick = "heads_up_refresh_click",
  HeadsUpRegenerateClick = "heads_up_regenerate_click",
  HeadsUpFindMoreClick = "heads_up_find_more_click",
  HeadsUpLiveSessionStart = "heads_up_live_session_start",
  HeadsUpLiveSessionEnd = "heads_up_live_session_end",
  HeadsUpLiveEntryAppear = "heads_up_live_entry_appear",
  HeadsUpLiveExplainerOpen = "heads_up_live_explainer_open",
  HeadsUpLiveEntryFeedbackSubmit = "heads_up_live_entry_feedback_submit",

  // App and UI Readiness
  AppStoreInitialize = "app_store_initialize",
  UiReady = "ui_ready",
  LegacyBrowserBlocked = "legacy_browser_blocked",

  // Standrdized Experiment
  ExperimentStarted = "experiment_started",

  // Getting Started Guide
  GettingStartedStepCompleted = "getting_started_step_completed",
  GettingStartedDismissed = "getting_started_dismissed",
  GettingStartedToggled = "getting_started_toggled",
  GettingStartedAllComplete = "getting_started_all_complete",
  GettingStartedOnboardingTooltipDismissed = "getting_started_onboarding_tooltip_dismissed",

  // Editor
  ClipboardPaste = "clipboard_paste",
  EditorImageHoverToolbarOpen = "editor_image_hover_toolbar_open",
  EditorImageHoverToolbarDownload = "editor_image_hover_toolbar_download",
  OcrMenuOpen = "ocr_menu_open",
  OcrInsertClick = "ocr_insert_click",

  // System Permissions
  MeetingNotificationsSetupView = "meeting_notifications_setup_view",
  MeetingNotificationsSetupSubmit = "meeting_notifications_setup_submit",
  VoiceSetupView = "voice_setup_view",
  VoiceSetupSubmit = "voice_setup_submit",
  CalendarConnectStart = "calendar_connect_start",
  CalendarConnectComplete = "calendar_connect_complete",
  CalendarConnectFailed = "calendar_connect_failed",
  CalendarDisconnectStart = "calendar_disconnect_start",
  CalendarDisconnectComplete = "calendar_disconnect_complete",
  CalendarToggle = "calendar_toggle",
  CalendarRefreshManual = "calendar_refresh_manual",
  NoteLinkToCalendarEvent = "note_link_to_calendar_event",
  CalendarEventJoin = "calendar_event_join",
  CalendarEventOpen = "calendar_event_open",
  CalendarIntegrationShillHide = "calendar_integration_shill_hide",

  // Onboarding - Calendar Setup
  OnboardingCalendarSetupSkipped = "onboarding_calendar_setup_skipped",
  OnboardingCalendarSetupComplete = "onboarding_calendar_setup_complete",

  // Meeting Briefing
  MeetingBriefingView = "meeting_briefing_view",
  MeetingBriefingRegenerate = "meeting_briefing_regenerate",
  MeetingBriefingFeedbackSubmit = "meeting_briefing_feedback_submit",
  MeetingBriefingNoteChipInteract = "meeting_briefing_note_chip_interact",

  // PDF Viewer
  PdfViewerOpen = "pdf_viewer_open",
  PdfViewerPageChange = "pdf_viewer_page_change",
  PdfViewerEmbedToggle = "pdf_viewer_embed_toggle",
  PdfViewerClose = "pdf_viewer_close",

  // Image Viewer
  ImageViewerOpen = "image_viewer_open",
  ImageViewerClose = "image_viewer_close",

  // PDF Upload
  PdfUploadStart = "pdf_upload_start",
  PdfUploadValidationFailed = "pdf_upload_validation_failed",
  PdfUploadSuccess = "pdf_upload_success",
  PdfUploadFailed = "pdf_upload_failed",
  PdfPageLimitWarningShown = "pdf_page_limit_warning_shown",
  PdfPageLimitWarningConfirmed = "pdf_page_limit_warning_confirmed",
  PdfPageLimitWarningCancelled = "pdf_page_limit_warning_cancelled",
}

export enum TrackedEventContext {
  FloatingBar = "floating_bar",
  Notes = "notes",
  InsertMenu = "insert_menu",
}

// Monetization contexts for paywall-related events
export enum PaywallContext {
  NoteCreate = "note_create",
  NoteMentionCreate = "note_mention_create",
  ChatMessageSend = "chat_message_send",
  NoteList = "note_list",
  CollectionList = "collection_list",
  DarkModeToggle = "dark_mode_toggle",
  ChatModelSelection = "chat_model_selection",
  ContentUnderstandingPdfPage = "content_understanding_pdf_page",
}

export type FeatureGateLevel = "warning" | "exceeded";

export interface MonetizationUsageEventParams {
  note_create_quota_used?: number;
  note_create_quota_limit?: number | typeof Infinity;
  note_create_usage_ratio?: number;
  chat_message_send_quota_used?: number;
  chat_message_send_quota_limit?: number | typeof Infinity;
  chat_message_send_chat_usage_ratio?: number;
}

export interface FeatureGateEventParams {
  feature: GatedFeature;
  level: FeatureGateLevel;
  used: number;
  limit: number | typeof Infinity;
  warn_at: number | null;
}

export interface PaywallEventParams
  extends MonetizationUsageEventParams,
    Partial<FeatureGateEventParams> {
  context?: PaywallContext | string;
}

export interface FeatureGateDeniedEventParams extends FeatureGateEventParams {
  context?: PaywallContext | string;
}

export interface VoiceSessionEventParams {
  audio_recording_id: string;
  note_id: string | null;
  recording_strategy: AudioRecordingStrategy | null;
  has_calendar_event_source: boolean;
  is_meeting_recording: boolean;
}

// Tab Navigation Event Parameters
export interface PaneSplitEventParams {
  origin_pane_index: number | string;
}

export interface TabOpenEventParams {
  view_id: string;
  pane_index: number;
}

export interface TabViewEventParams {
  view_id: string;
  pane_index: number;
}

export interface ExperimentStartedEventParams {
  experiment: string;
  variant: string;
  context?: Record<string, unknown>;
}

export interface SidePanelViewEventParams {
  variant: "control" | "treatment";
}

// Getting Started Event Parameters
export interface GettingStartedStepCompletedEventParams {
  step_id: string;
  step_title: string;
  completed_count: number;
  total_count: number;
}

export interface GettingStartedToggledEventParams {
  is_expanded: boolean;
}

// OneClick Import Event Parameters
export interface OneClickImportListViewEventParams {
  source: OneClickImportSource;
  total_notes_count: number;
  selectable_notes_count: number;
  locked_notes_count: number;
  entry_point: OneClickImportEntryPoint;
}

export interface OneClickImportItemSelectEventParams {
  source: OneClickImportSource;
  note_title: string;
  note_subtitle: string | undefined;
  selected_count: number;
  total_count: number;
  entry_point: OneClickImportEntryPoint;
}

export interface OneClickImportItemDeselectEventParams {
  source: OneClickImportSource;
  note_title: string;
  note_subtitle: string | undefined;
  selected_count: number;
  total_count: number;
  entry_point: OneClickImportEntryPoint;
}

export interface OneClickImportCancelEventParams {
  source: OneClickImportSource;
  selected_count: number;
  total_count: number;
  entry_point: OneClickImportEntryPoint;
}

export interface OneClickImportSubmitEventParams {
  source: OneClickImportSource;
  selected_count: number;
  total_count: number;
  use_folders_as_collections: boolean;
  use_tags_as_collections: boolean;
  /** Total number of folders available in imported notes */
  total_folders_count: number;
  /** Number of folders user selected to convert to collections */
  selected_folders_count: number;
  /** Total number of tags available in imported notes */
  total_tags_count: number;
  /** Number of tags user selected to convert to collections */
  selected_tags_count: number;
  entry_point: OneClickImportEntryPoint;
}

export interface OneClickImportCompleteEventParams {
  source: OneClickImportSource;
  /** Whether the import succeeded */
  success: boolean;
  /** Number of notes that were imported */
  imported_count: number;
  /** Total time from import start to completion in milliseconds */
  duration_ms: number;
  /** Number of notes that had image attachments */
  notes_with_images_count: number;
  /** Number of notes that had non-image file attachments */
  notes_with_files_count: number;
  /** Total number of image attachments across all imported notes */
  total_images_count: number;
  /** Total number of non-image file attachments across all imported notes */
  total_files_count: number;
  entry_point: OneClickImportEntryPoint;
}

export interface OneClickImportLoadFailedEventParams {
  source: OneClickImportSource;
  entry_point: OneClickImportEntryPoint;
  /** Error message from the failed load attempt */
  error: string;
}

export type CalendarProvider = "google" | "outlook";
export type CalendarConnectSource = "ios" | "web" | "desktop";
export type CalendarConnectErrorType =
  | "oauth_denied"
  | "missing_code"
  | "state_mismatch"
  | "api_error";
export type CalendarSyncStatus = "SYNCING" | "TRYING_TO_CONNECT" | "SYNCED" | "FAILED";
export type { MeetingProvider } from "@/domains/calendar/getMeetingPlatformFromUrl";

export interface CalendarConnectStartEventParams {
  provider: CalendarProvider;
  source: CalendarConnectSource;
}

export interface CalendarConnectCompleteEventParams {
  provider: CalendarProvider;
  calendar_connection_id: string;
  source: CalendarConnectSource;
}

export interface CalendarConnectFailedEventParams {
  provider: CalendarProvider;
  error_type: CalendarConnectErrorType;
  error_message?: string;
  source: CalendarConnectSource;
}

export interface CalendarDisconnectStartEventParams {
  provider: CalendarProvider;
  calendar_connection_id: string;
}

export interface CalendarDisconnectCompleteEventParams {
  provider: CalendarProvider;
  calendar_connection_id: string;
}

export interface CalendarToggleEventParams {
  provider: CalendarProvider;
  connected_calendar_id: string;
  calendar_connection_id: string;
  enabled: boolean;
}

export interface CalendarRefreshManualEventParams {
  provider: CalendarProvider;
  connected_calendar_id: string;
  calendar_connection_id: string;
  previous_sync_status: CalendarSyncStatus;
}

export interface NoteLinkToCalendarEventParams {
  note_id: string;
  connected_calendar_event_id: string;
  context: EventContext;
}

export interface CalendarEventJoinEventParams {
  connected_calendar_event_id: string;
  meeting_provider: MeetingProvider;
}

export interface CalendarEventOpenEventParams {
  connected_calendar_event_id: string;
}

export interface ChatModelSelectEventParams {
  selectedModel: string;
  previousModel: string;
  startsNewChat: boolean;
  choseOutOfTierModel: boolean;
}

export interface ChatModelDropdownOpenEventParams {
  currentModel: string;
}

export interface ChatGlobalInstructionsSaveEventParams {
  instructions: string;
}

export interface ChatSessionMessageSendEventParams {
  session_id: string;
  message_contents: string;
  selectedModel: ChatModel;
  model_family: ChatModelFamily;
}

export interface ChatSessionMessageFeedbackEventParams {
  event_id: string;
  session_id: string;
  event_kind: string;
  selected_model: ChatModel;
}

export interface ChromeExtensionModelSelectEventParams {
  selectedModel: ChatModel;
  previousModel: ChatModel;
}

export interface ChromeExtensionModelDropdownOpenEventParams {
  currentModel: ChatModel;
}

export interface EditorImageHoverToolbarOpenEventParams {
  note_id: string;
  image_id: string | null;
}

export interface EditorImageHoverToolbarDownloadEventParams {
  note_id: string;
  image_id: string | null;
}

export type OcrInsertPosition = "above" | "below";

export interface OcrMenuOpenEventParams {
  image_id: string | null;
  note_id: string;
}

export interface OcrInsertClickEventParams {
  image_id: string | null;
  note_id: string;
  position: OcrInsertPosition;
}

// Meeting Briefing Event Parameters
export type MeetingBriefingNoteChipAction = "click" | "hover";

export interface MeetingBriefingViewEventParams {
  connected_calendar_event_id: string | null;
  note_ids: string[];
  markdown: string | null;
}

export interface MeetingBriefingRegenerateEventParams {
  connected_calendar_event_id: string;
  active_note_id: string;
}

export interface MeetingBriefingFeedbackSubmitEventParams {
  connected_calendar_event_id: string;
  response: "UPVOTE" | "DOWNVOTE";
  comment: string | null;
  note_ids: string[];
  markdown: string | null;
}

export interface MeetingBriefingNoteChipInteractEventParams {
  connected_calendar_event_id: string | null;
  action: MeetingBriefingNoteChipAction;
  note_id: string;
  note_title: string;
}

export interface OnboardingStepChangeEventParams {
  from_step_id: string | undefined;
  to_step_id: string | undefined;
  completed_count: number;
  total_count: number;
}

export interface OnboardingSubstepChangeEventParams {
  step_id: string | undefined;
  from_substep_id: string | undefined;
  to_substep_id: string | undefined;
}

export interface OnboardingCompleteEventParams {
  total_steps: number;
}

export interface HeadsUpAnalyticsContextEventParams {
  context_id?: string;
  context_note_id?: string;
  context_note_title?: string;
  smart_view_id?: string;
  smart_view_kind: string;
  active_space_account_id?: string;
}

export type HeadsUpLiveSessionEndReason =
  | "recording_stopped"
  | "context_changed"
  | "note_changed"
  | "live_unavailable"
  | "unmount";

export interface HeadsUpLiveSessionStartEventParams extends HeadsUpAnalyticsContextEventParams {
  recording_id: string;
}

export interface HeadsUpLiveSessionEndEventParams extends HeadsUpAnalyticsContextEventParams {
  recording_id: string;
  duration_ms: number;
  end_reason: HeadsUpLiveSessionEndReason;
}

export interface HeadsUpLiveEntryAppearEventParams extends HeadsUpAnalyticsContextEventParams {
  smart_card_id: string;
  entry_id: string;
  note_id: string;
  note_title: string;
  topic: string;
  insight: string | null;
  item_position_index: number;
  quote: string | null;
  has_feedback_controls: boolean;
  recording_offset_seconds: number | null;
}

export type HeadsUpLiveExplainerSource = "content_card" | "empty_state";

export interface HeadsUpLiveExplainerOpenEventParams extends HeadsUpAnalyticsContextEventParams {
  source: HeadsUpLiveExplainerSource;
  smart_card_id: string | null;
  is_live_active: boolean;
}

export interface HeadsUpLiveEntryFeedbackSubmitEventParams
  extends HeadsUpAnalyticsContextEventParams {
  smart_card_id: string;
  entry_id: string;
  note_id: string;
  note_title: string;
  response: "UPVOTE" | "DOWNVOTE";
  comment: string | null;
  topic: string;
  insight: string | null;
  has_quote: boolean;
  recording_offset_seconds: number | null;
}

// PDF Viewer Event Parameters
export type PdfViewerOpenContext = "editor" | "search_result" | "chat";

export type PdfViewerPageChangeMethod =
  | "arrow_button"
  | "keyboard"
  | "scroll"
  | "carousel"
  | "page_input";

export interface PdfViewerOpenEventParams {
  context: PdfViewerOpenContext;
}

export interface PdfViewerPageChangeEventParams {
  context: PdfViewerOpenContext;
  page: number;
  total_pages: number;
  method: PdfViewerPageChangeMethod;
}

export interface PdfViewerEmbedToggleEventParams {
  context: PdfViewerOpenContext;
  is_visible: boolean;
}

export interface PdfViewerCloseEventParams {
  context: PdfViewerOpenContext;
  pages_viewed: number;
  total_pages: number;
  session_duration_ms: number;
}

// Image Viewer Event Parameters
export type ImageViewerOpenContext = "editor" | "search_result" | "chat";

export interface ImageViewerOpenEventParams {
  context: ImageViewerOpenContext;
}

export interface ImageViewerCloseEventParams {
  context: ImageViewerOpenContext;
  session_duration_ms: number;
}

// PDF Upload Event Parameters
export type PdfUploadSource = "drag_drop" | "file_picker" | "paste";

export type PdfUploadValidationReason = "too_large" | "invalid_mime" | "page_limit";

export interface PdfUploadStartEventParams {
  context: PdfViewerOpenContext;
  source: PdfUploadSource;
  file_size_bytes: number;
  mime_type: string;
}

export interface PdfUploadValidationFailedEventParams {
  context: PdfViewerOpenContext;
  reason: PdfUploadValidationReason;
  file_size_bytes?: number;
  limit_bytes?: number;
}

export interface PdfUploadSuccessEventParams {
  context: PdfViewerOpenContext;
  file_size_bytes: number;
  duration_ms: number;
  attachment_id: string;
}

export interface PdfUploadFailedEventParams {
  context: PdfViewerOpenContext;
  file_size_bytes: number;
  duration_ms: number;
  error_code: string;
  http_status?: number;
}

export interface PdfPageLimitWarningShownEventParams {
  page_count: number;
  page_limit: number;
}

export interface PdfPageLimitWarningConfirmedEventParams {
  page_count: number;
  page_limit: number;
  visible_duration_ms: number;
}

export interface PdfPageLimitWarningCancelledEventParams {
  page_count: number;
  page_limit: number;
  visible_duration_ms: number;
}

/**
 * Type-safe mapping of TrackedEvent to their required properties.
 * Events not listed here will fall back to Record<string, unknown>.
 */
export type TrackedEventProperties = {
  [TrackedEvent.PaywallView]: PaywallEventParams;
  [TrackedEvent.PaywallCancelClick]: PaywallEventParams;
  [TrackedEvent.PaywallUpgradeClick]: PaywallEventParams;
  [TrackedEvent.FeatureGateBannerView]: FeatureGateEventParams;
  [TrackedEvent.FeatureGateBannerUpgradeClick]: FeatureGateEventParams;
  [TrackedEvent.FeatureGateDenied]: FeatureGateDeniedEventParams;
  [TrackedEvent.VoiceSessionStart]: VoiceSessionEventParams;
  [TrackedEvent.VoiceSessionPause]: VoiceSessionEventParams;
  [TrackedEvent.VoiceSessionResume]: VoiceSessionEventParams;
  [TrackedEvent.VoiceSessionDone]: VoiceSessionEventParams;
  [TrackedEvent.ExperimentStarted]: ExperimentStartedEventParams;
  [TrackedEvent.PaneSplit]: PaneSplitEventParams;
  [TrackedEvent.TabOpen]: TabOpenEventParams;
  [TrackedEvent.TabView]: TabViewEventParams;
  [TrackedEvent.SidePanelView]: SidePanelViewEventParams;
  [TrackedEvent.GettingStartedStepCompleted]: GettingStartedStepCompletedEventParams;
  [TrackedEvent.GettingStartedToggled]: GettingStartedToggledEventParams;
  [TrackedEvent.OneClickImportListView]: OneClickImportListViewEventParams;
  [TrackedEvent.OneClickImportItemSelect]: OneClickImportItemSelectEventParams;
  [TrackedEvent.OneClickImportItemDeselect]: OneClickImportItemDeselectEventParams;
  [TrackedEvent.OneClickImportCancel]: OneClickImportCancelEventParams;
  [TrackedEvent.OneClickImportSubmit]: OneClickImportSubmitEventParams;
  [TrackedEvent.OneClickImportComplete]: OneClickImportCompleteEventParams;
  [TrackedEvent.OneClickImportLoadFailed]: OneClickImportLoadFailedEventParams;
  [TrackedEvent.CalendarConnectStart]: CalendarConnectStartEventParams;
  [TrackedEvent.CalendarConnectComplete]: CalendarConnectCompleteEventParams;
  [TrackedEvent.CalendarConnectFailed]: CalendarConnectFailedEventParams;
  [TrackedEvent.CalendarDisconnectStart]: CalendarDisconnectStartEventParams;
  [TrackedEvent.CalendarDisconnectComplete]: CalendarDisconnectCompleteEventParams;
  [TrackedEvent.CalendarToggle]: CalendarToggleEventParams;
  [TrackedEvent.CalendarRefreshManual]: CalendarRefreshManualEventParams;
  [TrackedEvent.NoteLinkToCalendarEvent]: NoteLinkToCalendarEventParams;
  [TrackedEvent.CalendarEventJoin]: CalendarEventJoinEventParams;
  [TrackedEvent.CalendarEventOpen]: CalendarEventOpenEventParams;
  [TrackedEvent.CalendarIntegrationShillHide]: Record<string, never>;
  [TrackedEvent.ChatModelSelect]: ChatModelSelectEventParams;
  [TrackedEvent.ChatModelDropdownOpen]: ChatModelDropdownOpenEventParams;
  [TrackedEvent.ChatGlobalInstructionsSave]: ChatGlobalInstructionsSaveEventParams;
  [TrackedEvent.ChromeExtensionModelSelect]: ChromeExtensionModelSelectEventParams;
  [TrackedEvent.ChromeExtensionModelDropdownOpen]: ChromeExtensionModelDropdownOpenEventParams;
  [TrackedEvent.EditorImageHoverToolbarOpen]: EditorImageHoverToolbarOpenEventParams;
  [TrackedEvent.EditorImageHoverToolbarDownload]: EditorImageHoverToolbarDownloadEventParams;
  [TrackedEvent.OcrMenuOpen]: OcrMenuOpenEventParams;
  [TrackedEvent.OcrInsertClick]: OcrInsertClickEventParams;
  [TrackedEvent.ChatSessionMessageSend]: ChatSessionMessageSendEventParams;
  [TrackedEvent.ChatSessionMessageUpvote]: ChatSessionMessageFeedbackEventParams;
  [TrackedEvent.ChatSessionMessageDownvote]: ChatSessionMessageFeedbackEventParams;
  [TrackedEvent.MeetingBriefingView]: MeetingBriefingViewEventParams;
  [TrackedEvent.MeetingBriefingRegenerate]: MeetingBriefingRegenerateEventParams;
  [TrackedEvent.MeetingBriefingFeedbackSubmit]: MeetingBriefingFeedbackSubmitEventParams;
  [TrackedEvent.MeetingBriefingNoteChipInteract]: MeetingBriefingNoteChipInteractEventParams;
  [TrackedEvent.HeadsUpLiveSessionStart]: HeadsUpLiveSessionStartEventParams;
  [TrackedEvent.HeadsUpLiveSessionEnd]: HeadsUpLiveSessionEndEventParams;
  [TrackedEvent.HeadsUpLiveEntryAppear]: HeadsUpLiveEntryAppearEventParams;
  [TrackedEvent.HeadsUpLiveExplainerOpen]: HeadsUpLiveExplainerOpenEventParams;
  [TrackedEvent.HeadsUpLiveEntryFeedbackSubmit]: HeadsUpLiveEntryFeedbackSubmitEventParams;
  [TrackedEvent.OnboardingStepChange]: OnboardingStepChangeEventParams;
  [TrackedEvent.OnboardingSubstepChange]: OnboardingSubstepChangeEventParams;
  [TrackedEvent.OnboardingComplete]: OnboardingCompleteEventParams;
  [TrackedEvent.PdfViewerOpen]: PdfViewerOpenEventParams;
  [TrackedEvent.PdfViewerPageChange]: PdfViewerPageChangeEventParams;
  [TrackedEvent.PdfViewerEmbedToggle]: PdfViewerEmbedToggleEventParams;
  [TrackedEvent.PdfViewerClose]: PdfViewerCloseEventParams;
  [TrackedEvent.ImageViewerOpen]: ImageViewerOpenEventParams;
  [TrackedEvent.ImageViewerClose]: ImageViewerCloseEventParams;
  [TrackedEvent.PdfUploadStart]: PdfUploadStartEventParams;
  [TrackedEvent.PdfUploadValidationFailed]: PdfUploadValidationFailedEventParams;
  [TrackedEvent.PdfUploadSuccess]: PdfUploadSuccessEventParams;
  [TrackedEvent.PdfUploadFailed]: PdfUploadFailedEventParams;
  [TrackedEvent.PdfPageLimitWarningShown]: PdfPageLimitWarningShownEventParams;
  [TrackedEvent.PdfPageLimitWarningConfirmed]: PdfPageLimitWarningConfirmedEventParams;
  [TrackedEvent.PdfPageLimitWarningCancelled]: PdfPageLimitWarningCancelledEventParams;
};

/**
 * Helper type to get properties for a specific TrackedEvent.
 * Falls back to Record<string, unknown> for events without specific properties.
 */
export type PropertiesForEvent<T extends TrackedEvent> = T extends keyof TrackedEventProperties
  ? TrackedEventProperties[T]
  : Record<string, unknown>;
